import React, { useState } from "react";
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { editAllWorkerDefaults, overrideAllWorkerJobDefaults} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { toast } from "react-toastify";
import { parseTime, toTimeString } from "../../utils";
import { confirmDialog } from "../../../../components/ConfirmDialog/confirmDialog";

type FormValues = {
    commutingHours: string;
    commutingMinutes: string;
    workload: number;
    attention: number;
    breakFrequencyHours?: string;
    breakFrequencyMinutes?: string;
    averageBreakHours?: string;
    averageBreakMinutes?: string;
    longestWorkHours?: string;
    longestWorkMinutes?: string;
    longestBreakHours?: string;
    longestBreakMinutes?: string;
}

const WorkerDefaults = ({workerId, defaults, handleClose}: any) => {
    const {state} = useAuth();
    const [loading, setLoading] = useState(false);

    const {register, handleSubmit, control} = useForm<FormValues>({
        defaultValues: {
            commutingHours: parseTime(defaults.commutingTime).hours,
            commutingMinutes: parseTime(defaults.commutingTime).minutes,
            workload: defaults.workload,
            attention: defaults.attention,
            breakFrequencyHours: parseTime(defaults.breakFrequency).hours,
            breakFrequencyMinutes: parseTime(defaults.breakFrequency).minutes,
            averageBreakHours: parseTime(defaults.averageLengthOfBreaks).hours,
            averageBreakMinutes: parseTime(defaults.averageLengthOfBreaks).minutes,
            longestWorkHours: parseTime(defaults.longestWorkPeriodOfContinuousWork).hours,
            longestWorkMinutes: parseTime(defaults.longestWorkPeriodOfContinuousWork).minutes,
            longestBreakHours: parseTime(defaults.breakLengthAfterLongestWorkPeriod).hours,
            longestBreakMinutes: parseTime(defaults.breakLengthAfterLongestWorkPeriod).minutes,
        }
    });

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const response = await editAllWorkerDefaults(state.user?.token, {
                workerId: workerId,
                commutingTime: toTimeString(data.commutingHours, data.commutingMinutes),
                workload: Number(data.workload),
                attention: Number(data.attention),
                breakFrequency: toTimeString(data.breakFrequencyHours, data.breakFrequencyMinutes),
                averageLengthOfBreaks: toTimeString(data.averageBreakHours, data.averageBreakMinutes),
                longestWorkPeriodOfContinuousWork: toTimeString(data.longestWorkHours, data.longestWorkMinutes),
                breakLengthAfterLongestWorkPeriod: toTimeString(data.longestBreakHours, data.longestBreakMinutes),
            });

            if (response) {
                handleClose();
                toast.success("All worker defaults updated successfully");
                setLoading(true);
            }
        } catch (error) {
            toast.error("Failed to update worker defaults. Please try again.");
        }
    };

    const overrideDefaults = async () => {
        const response = await overrideAllWorkerJobDefaults(state.user?.token, workerId);
        if (response) {
            handleClose();
            toast.success("All worker defaults updated successfully");
        }
        console.log(response);
    }

    return (
        <>
            <DialogTitle
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "1.6rem",
                  letterSpacing: 0.5,
                  color: "#1e293b",
                  pb: 2,
                }}
            >
                Fatigue / Risk Assessment
            </DialogTitle>
            <DialogContent dividers sx={{ px: 4, py: 3 }}>
                <Grid container spacing={4}>
                  {/* LEFT SIDE */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Section
                            title="Commuting Time"
                            description="What is the typical commuting time of employees to or from work?"
                        >
                            <Box display="flex" gap={2} alignItems="center">
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("commutingHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("commutingMinutes")}
                                />
                            </Box>
                            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                                (Please specify the typical commuting time)
                            </Typography>
                        </Section>

                        {/* TYPE OF JOB - WORKLOAD */}
                        <Section
                            title="Type of Job: Workload"
                            description="The workload and/or work pace of the job is typically:"
                        >
                            <Controller
                                name="workload"
                                control={control}
                                render={({ field }) => (
                            <RadioGroup {...field}>
                                <FormControlLabel
                                    value={3}
                                    control={<Radio />}
                                    label="Extremely demanding, no spare capacity"
                                />
                                <FormControlLabel
                                    value={2}
                                    control={<Radio />}
                                    label="Moderately demanding, little spare capacity"
                                />
                                <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label="Moderately undemanding, some spare capacity"
                                />
                                <FormControlLabel
                                    value={0}
                                    control={<Radio />}
                                    label="Extremely undemanding, lots of spare capacity"
                                />
                            </RadioGroup>
                              )}
/>
                        </Section>

                        <Section
                            title="Type of Job: Attention"
                            description="The job typically requires continuous attention:"
                        >
                            <Controller
                                name="attention"
                                control={control}
                                render={({ field }) => (
                            <RadioGroup {...field}>
                                <FormControlLabel
                                    value={3}
                                    control={<Radio />}
                                    label="All or nearly all the time"
                                />
                                <FormControlLabel
                                    value={2}
                                    control={<Radio />}
                                    label="Most of the time"
                                />
                                <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label="Some of the time"
                                />
                                <FormControlLabel
                                    value={0}
                                    control={<Radio />}
                                    label="Rarely or nearly none of the time"
                                />
                            </RadioGroup>
                                )}/>
                        </Section>
                    </Grid>

                  {/* RIGHT SIDE */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    {/* BREAKS */}
                        <Section
                            title="Breaks"
                        >
                            <Typography variant="body2" mt={4} mb={1}>
                                How frequently (to the nearaset 15 mins) are rest breaks typically provided OR taken?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("breakFrequencyHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("breakFrequencyMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                                What is the typical average length of these breaks (to the nearest 5 minutes) that are provided or taken?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                   {...register("averageBreakHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("averageBreakMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                                What is typically the longest (to the nearest 15 mins) period of continuous work before
                                a break?
                            </Typography>
                            <Box display="flex" gap={2} mb={4}>
                                <TextField
                                    label="Hours"
                                    type="number"
                                    size="small"
                                    {...register("longestWorkHours")}
                                />
                                <TextField
                                    label="Minutes"
                                    type="number"
                                    size="small"
                                    {...register("longestWorkMinutes")}
                                />
                            </Box>

                            <Typography variant="body2" mb={1}>
                              What is typically the length of the break taken after this
                              longest period of continuous work (to the nearest 5 minutes)?
                            </Typography>
                            <Box display="flex" gap={2} mb={2}>
                                <TextField
                                  label="Hours"
                                  type="number"
                                  size="small"
                                  {...register("longestBreakHours")}
                                />
                                <TextField
                                  label="Minutes"
                                  type="number"
                                  size="small"
                                  {...register("longestBreakMinutes")}
                                />
                            </Box>
                        </Section>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography
                    variant="body2"
                    textAlign="center"
                    color="text.secondary"
                    sx={{
                      maxWidth: 600,
                      mx: "auto",
                      fontStyle: "italic",
                    }}
                >
                    Please note that the answers you provide above will be set as the 'default'
                    for the entire work schedule, but that you can alter the value(s) for any given duty if
                    appropriate.
                </Typography>
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    py: 2,
                }}
            >
               <Button
                    variant="text"
                    onClick={() => 
                        confirmDialog({
                            title: `Are you sure you want to override all defaults?`,
                            onOk: () => overrideDefaults(),
                            okButtonProps: { danger: true },
                            okText: 'YES',
                            cancelText: 'NO',
                            centered: true
                        })
                    }
                    sx={{
                        position: "absolute",
                        left: 24,
                        color: "#ca0707ff",
                        textDecoration: "underline",
                        textTransform: "none",
                        fontWeight: 500,
                        boxShadow: "none",
                        "&:hover": { background: "transparent", color: "#ca0707ff", textDecoration: "underline" }
                    }}
                >
                    Override All Defaults
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit(onSubmit)}
                    style={{
                      width: 180,
                      color: 'white',
                      boxShadow:'none',
                      textTransform: "none",
                      backgroundColor: "#156bc2",
                    }}
                >
                    {loading ? (
                        <> <CircularProgress size={25} style={{color: 'white'}} /></>
                    ) : "Save"}
                </Button>
            </DialogActions>
        </>
    );
};

export default WorkerDefaults;

interface SectionProps {
    title: string;
    description?: string;
    children?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
    title,
    description,
    children,
}) => {
    return (
        <Box
            sx={{
                mb: 1.5,
                p: 2.5,
                borderRadius: 2,
                backgroundColor: "white",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.025)",
                transition: "all 0.3s",
                "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
            }}
        >
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Typography variant="h6" fontWeight={600}>
                    {title}
                </Typography>
            </Box>

            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.6 }}
                >
                  {description}
                </Typography>
            )}

            {children}
        </Box>
    );
};
